import {
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  FacebookAuthProvider,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  RecaptchaVerifier,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  TwitterAuthProvider,
  unlink,
  updatePassword,
} from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  documentId,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  startAfter,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getAuth, initializeRecaptchaConfig as preloadRecaptchaConfig } from 'firebase/auth';

import { DEFAULT_PAGE_SIZE } from '../constants/pagination';
import { USER } from '../constants/roles';
import { auth, db, firebaseApp } from './client';

export { RecaptchaVerifier, signInWithPhoneNumber };

export function createRecaptchaVerifier(containerOrId, parameters) {
  return new RecaptchaVerifier(auth, containerOrId, parameters);
}

export function setPhoneAuthTestMode(enabled) {
  if (enabled && auth.settings) {
    auth.settings.appVerificationDisabledForTesting = true;
  }
}

export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();
export const twitterProvider = new TwitterAuthProvider();

function buildAuthUserPayload(authUser, userDoc) {
  return {
    uid: authUser.uid,
    emailVerified: authUser.emailVerified,
    providerData: authUser.providerData,
    phoneNumber: authUser.phoneNumber,
    ...userDoc,
    roles: userDoc.roles || {},
    ssn: null,
  };
}

export async function fetchAuthUserProfile(authUser) {
  const userSnapshot = await getDoc(doc(db, 'users', authUser.uid));
  const user = userSnapshot.data();

  if (!user) {
    const roles = { [USER]: USER };
    await setDoc(
      doc(db, 'users', authUser.uid),
      {
        uid: authUser.uid,
        email: '',
        roles,
        phoneNumber: authUser.phoneNumber,
      },
      { merge: true }
    );

    const newUserSnapshot = await getDoc(doc(db, 'users', authUser.uid));
    const newUser = newUserSnapshot.data() || {
      uid: authUser.uid,
      email: '',
      roles,
      phoneNumber: authUser.phoneNumber,
    };

    return buildAuthUserPayload(authUser, {
      ...newUser,
      roles: newUser.roles || roles,
    });
  }

  return buildAuthUserPayload(authUser, user);
}

export function createAuthUserListener(next, fallback) {
  return onAuthStateChanged(auth, async (authUser) => {
    if (!authUser) {
      fallback();
      return;
    }

    const profile = await fetchAuthUserProfile(authUser);
    next(profile);
  });
}

export const doCreateUserWithEmailAndPassword = (email, password) =>
  createUserWithEmailAndPassword(auth, email, password);

export const doSignInWithEmailAndPassword = (email, password) =>
  signInWithEmailAndPassword(auth, email, password);

export const doSignInWithGoogle = () => signInWithPopup(auth, googleProvider);
export const doSignInWithFacebook = () => signInWithPopup(auth, facebookProvider);
export const doSignInWithTwitter = () => signInWithPopup(auth, twitterProvider);
export const doSignOut = () => signOut(auth);
export const doPasswordReset = (email) => sendPasswordResetEmail(auth, email);

export const doSendEmailVerification = () =>
  sendEmailVerification(auth.currentUser, {
    url: 'https://zenreki.cash/Dashboard',
  });

export const doPasswordUpdate = (password) => updatePassword(auth.currentUser, password);

export const fetchSignInMethods = (email) => fetchSignInMethodsForEmail(auth, email);

export const doLinkWithPopup = (provider) => linkWithPopup(auth.currentUser, provider);

export const doLinkWithEmailPassword = (email, password) =>
  linkWithCredential(auth.currentUser, EmailAuthProvider.credential(email, password));

export const doUnlinkProvider = (providerId) => unlink(auth.currentUser, providerId);

export const doDeleteCurrentUser = () => deleteUser(auth.currentUser);

export function getFriendlyAuthError(error) {
  if (!error || !error.code) {
    return error ? error.message : 'An unknown error occurred.';
  }

  switch (error.code) {
    case 'auth/too-many-requests':
      return 'Too many requests. Please wait a few minutes before trying again.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in or resetting your password.';
    case 'auth/invalid-email':
      return 'The email address is not valid.';
    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 6 characters.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/requires-recent-login':
      return 'This action requires recent login. Please sign in again.';
    default:
      return error.message;
  }
}

export async function deleteMessages(userId) {
  const messagesQuery = query(collection(db, 'messages'), where('userId', '==', userId));
  const snapshot = await getDocs(messagesQuery);
  if (snapshot.empty) {
    return undefined;
  }

  const batch = writeBatch(db);
  snapshot.forEach((messageDoc) => batch.delete(messageDoc.ref));
  return batch.commit();
}

async function commitDocDeletes(docRefs) {
  if (!docRefs.length) {
    return;
  }

  for (let index = 0; index < docRefs.length; index += 500) {
    const batch = writeBatch(db);
    docRefs.slice(index, index + 500).forEach((docRef) => batch.delete(docRef));
    await batch.commit();
  }
}

async function orphanCoinsForUser(userId) {
  const ownedCoinsSnap = await getDocs(
    query(collection(db, 'coins'), where('owner', '==', userId))
  );

  if (ownedCoinsSnap.empty) {
    return 0;
  }

  const coinDocs = ownedCoinsSnap.docs;

  for (let index = 0; index < coinDocs.length; index += 500) {
    const batch = writeBatch(db);
    coinDocs.slice(index, index + 500).forEach((coinDoc) => {
      batch.update(coinDoc.ref, { owner: '' });
    });
    await batch.commit();
  }

  return coinDocs.length;
}

async function deleteVisitorAccessForUser(userId) {
  const [ownerAccessSnap, visitorAccessSnap] = await Promise.all([
    getDocs(query(collection(db, 'coin_visitor_access'), where('ownerId', '==', userId))),
    getDocs(query(collection(db, 'coin_visitor_access'), where('visitorId', '==', userId))),
  ]);

  await commitDocDeletes([
    ...ownerAccessSnap.docs.map((accessDoc) => accessDoc.ref),
    ...visitorAccessSnap.docs.map((accessDoc) => accessDoc.ref),
  ]);
}

async function deleteSocialLinksForUser(userId) {
  const socialSnap = await getDocs(collection(db, 'users', userId, 'social'));
  await commitDocDeletes(socialSnap.docs.map((socialDoc) => socialDoc.ref));
}

export async function removeUserAsAdmin(userId) {
  const orphanedCoinCount = await orphanCoinsForUser(userId);
  await deleteVisitorAccessForUser(userId);
  await deleteMessages(userId);
  await deleteSocialLinksForUser(userId);
  await deleteDoc(doc(db, 'users', userId));

  return {
    userId,
    orphanedCoinCount,
    authDeleted: false,
  };
}

export async function removeOwnAccount() {
  const uid = auth.currentUser?.uid;

  if (!uid) {
    throw new Error('Sign in required.');
  }

  const result = await removeUserAsAdmin(uid);
  await doDeleteCurrentUser();

  return {
    ...result,
    authDeleted: true,
  };
}

export async function initializeRecaptchaConfig() {
  await preloadRecaptchaConfig(getAuth(firebaseApp));
}

export function userApi(uid) {
  const userRef = doc(db, 'users', uid);
  return {
    get: () => getDoc(userRef),
    set: (data, options = {}) => setDoc(userRef, data, { merge: options.merge !== false }),
    delete: () => deleteDoc(userRef),
    onSnapshot: (callback) => onSnapshot(userRef, callback),
  };
}

export function usersCollection() {
  return {
    onSnapshot: (callback) => onSnapshot(collection(db, 'users'), callback),
  };
}

export async function fetchAllUsers() {
  const snapshot = await getDocs(query(collection(db, 'users'), orderBy(documentId())));
  return snapshot.docs.map((userDoc) => ({
    ...userDoc.data(),
    uid: userDoc.id,
  }));
}

export async function saveUserProfile(uid, profile) {
  const { firstname, lastname, email, phoneNumber, roles } = profile;

  await setDoc(
    doc(db, 'users', uid),
    {
      uid,
      firstname: (firstname || '').trim(),
      lastname: (lastname || '').trim(),
      email: (email || '').trim(),
      phoneNumber: (phoneNumber || '').trim(),
      roles: roles || {},
    },
    { merge: true }
  );
}

export async function fetchUsersPage({ pageSize = DEFAULT_PAGE_SIZE, startAfterDoc = null } = {}) {
  const constraints = [orderBy(documentId())];

  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc));
  }

  constraints.push(limit(pageSize + 1));

  const usersQuery = query(collection(db, 'users'), ...constraints);
  const [snapshot, countSnapshot] = await Promise.all([
    getDocs(usersQuery),
    getCountFromServer(query(collection(db, 'users'))),
  ]);

  const docs = snapshot.docs;
  const hasNextPage = docs.length > pageSize;
  const pageDocs = hasNextPage ? docs.slice(0, pageSize) : docs;

  return {
    users: pageDocs.map((userDoc) => ({
      ...userDoc.data(),
      uid: userDoc.id,
    })),
    hasNextPage,
    endCursor: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
    totalCount: countSnapshot.data().count,
  };
}
